import { ShoppingCart, Package, User, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import LanguageSelector from './header/LanguageSelector';
import ThemeSelector from './header/ThemeSelector';
import ActAsSelector from './header/ActAsSelector';
import UserProfileMenu from './header/UserProfileMenu';

export default function Header() {
  const location = useLocation();
  const { getCartCount } = useCart();
  const { user, loading, signInWithGoogle } = useUser();
  const { theme, isAdmin, isSuperAdmin, isOverriding } = useTheme();
  const { t } = useLanguage();
  const cartCount = getCartCount();

  const headerStyle = theme.headerBackground
    ? { background: `hsl(${theme.headerBackground})` }
    : undefined;

  const navLinkClass = (active: boolean) => cn(
    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all',
    active
      ? 'bg-black/80 text-white'
      : theme.headerBackground
        ? 'text-black hover:bg-black/10'
        : 'text-white/90 hover:text-white hover:bg-white/10',
  );

  return (
    <header className="border-b border-primary/30 glass-header sticky top-0 z-50" style={headerStyle}>
      <div className="px-4 md:px-6 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              {theme.logo ? (
                <img src={theme.logo} alt={theme.name} className="h-10 px-3 py-2 bg-white rounded-xl shadow-md" />
              ) : (
                <>
                  <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-primary shadow-md">
                    <span className="text-primary-foreground font-bold text-xl">{theme.name[0]}</span>
                  </div>
                  <span className="text-xl font-bold text-white">{theme.name.toLowerCase()}</span>
                </>
              )}
              <span className="hidden sm:inline px-2 py-1 text-xs font-bold bg-black text-white rounded-full uppercase tracking-wide shadow">demo</span>
              {isOverriding && (
                <span className="px-2 py-1 text-xs font-bold bg-primary text-primary-foreground rounded-full uppercase tracking-wide">preview</span>
              )}
            </Link>
            {/* Phone number */}
            <a
              href="tel:+3197010223262"
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 shadow-lg hover:shadow-xl hover:scale-105 transition-all animate-pulse-subtle border-2 border-primary"
            >
              <Phone className="w-5 h-5 text-primary" />
              <div className="flex flex-col">
                <span className="text-xs text-gray-600">{t('voice.call_sophie')}</span>
                <span className="text-base font-bold tracking-wide">0970 - 102 232 62</span>
              </div>
            </a>
          </div>

          {/* Navigation */}
          <nav className="flex items-center gap-2 md:gap-4">
            <Link to="/bestellingen" className={navLinkClass(location.pathname === '/bestellingen')}>
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.orders')}</span>
            </Link>

            <Link to="/winkelwagen" className={cn(navLinkClass(location.pathname === '/winkelwagen'), 'relative')}>
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.cart')}</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-primary text-white text-xs font-bold rounded-full">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>

            <LanguageSelector />
            {isAdmin && <ThemeSelector />}
            {isSuperAdmin && <ActAsSelector />}

            {!loading && user && <UserProfileMenu />}

            {!loading && !user && (
              <button
                onClick={signInWithGoogle}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-all"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{t('header.login')}</span>
              </button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
